import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { SupportedBrands } from 'src/enums'
import { UserFeature, UserFeatureList } from 'src/models/user.models'
import { authQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  useExtendQuery,
  useRequestConfig,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectIsAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'

interface QueryUserFeaturesOptions
  extends ExtendBaseQueryOptions<
    { features: UserFeatureList },
    UserFeatureList
  > {}

export const useQueryUserFeatures = (
  features: UserFeature[],
  options?: QueryUserFeaturesOptions,
) => {
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const { data: user } = useQueryOwnUser({ enabled: isAuthenticated })
  const hasOrganization = !!user?.organizationId

  // TODO: remove once the backend includes BULK_CREDIT_CARD_ENROLLMENT in /features.
  // The backend does not yet recognize this key, so we strip it from the request
  // and hardcode `true` client-side.
  const requestedFeatures = features.filter(
    (f) => f !== UserFeature.BULK_CREDIT_CARD_ENROLLMENT,
  )
  const mockBulkCreditCardEnrollment = features.includes(
    UserFeature.BULK_CREDIT_CARD_ENROLLMENT,
  )

  // If after stripping mocked features the request is empty, the backend call
  // would be invalid — short-circuit with synthetic data so callers still
  // receive the mocked flag synchronously.
  const onlyMockedFeaturesRequested = requestedFeatures.length === 0

  return useExtendQuery({
    queryKey: authQueryKeys.features(features),
    queryUrl: () => `/features?features=${requestedFeatures.join(',')}`,
    select: (data) => ({
      ...data.features,
      ...(mockBulkCreditCardEnrollment && {
        [UserFeature.BULK_CREDIT_CARD_ENROLLMENT]: true,
      }),
    }),
    initialData: onlyMockedFeaturesRequested
      ? { features: {} as UserFeatureList }
      : undefined,
    ...options,
    enabled:
      !onlyMockedFeaturesRequested &&
      hasOrganization &&
      (options?.enabled ?? true),
  })
}

export type BrandRedirectResponse = {
  id: string
  name: string
  slug: SupportedBrands
  host: string
}

export const useQueryBrandRedirectSuggestions = (
  options?: Partial<UseQueryOptions<BrandRedirectResponse[]>>,
) => {
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const { data: user } = useQueryOwnUser({ enabled: isAuthenticated })
  const requestConfig = useRequestConfig()

  return useQuery<BrandRedirectResponse[]>({
    ...options,
    enabled: user?.email !== undefined && (options?.enabled ?? true),
    queryKey: ['brand_suggestions'],
    queryFn: async (): Promise<BrandRedirectResponse[]> => {
      return await axios
        .post(
          '/brandcheck',
          {
            email: user?.email,
          },
          { ...requestConfig, withCredentials: true },
        )
        .then((response) => {
          return response.data.brandCheck
            .suggestedBrands as BrandRedirectResponse[]
        })
    },
  })
}
